import type { DocumentNode } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { mergeSessionReviewPages, type SessionReviewItem } from '@src/components/sco/Advisors/reviews/sessionReviewHelpers';
import type { Query, ScoSessionReviewCollection, ScoUser } from '@src/graphql-types';
import { useCallback, useMemo, useState } from 'react';
type UseSessionReviewPagesOptions = {
  query: DocumentNode;
  /** Variables other than `page` / `pageSize`. */
  variables?: Record<string, unknown>;
  /** Picks the member (e.g. `data.userById` or `data.currentUser`). */
  selectUser: (data: Query | undefined) => ScoUser | null | undefined;
  pageSize?: number;
  skip?: boolean;
  /**
   * `no-cache` for the manage view (hidden text must never land on the normalized public review);
   * `cache-and-network` for public views so hide / feature changes show on the next visit.
   */
  fetchPolicy?: 'no-cache' | 'cache-and-network' | 'network-only' | 'cache-first';
};
type ExtraPages = {
  key: string;
  page: number;
  items: SessionReviewItem[];
  hasMore: boolean | null;
};
const NO_EXTRA_PAGES: Omit<ExtraPages, 'key'> = {
  page: 1,
  items: [],
  hasMore: null
};

/**
 * Page 1 comes from a watched query; later pages are fetched with `no-cache` and merged in React
 * (same approach as `useAdvisorsCollectionLoadMore`, but for a nested `ScoUser.sessionReviews`).
 */
export const useSessionReviewPages = ({
  query,
  variables = {},
  selectUser,
  pageSize = 5,
  skip = false,
  fetchPolicy = 'cache-and-network'
}: UseSessionReviewPagesOptions) => {
  const client = useApolloClient();
  // `variables` is usually a fresh object each render; key everything on its JSON.
  const variablesKey = JSON.stringify(variables);
  const baseVariables = useMemo(() => JSON.parse(variablesKey) as Record<string, unknown>, [variablesKey]);
  const result = useQuery<Query>(query, {
    variables: {
      ...baseVariables,
      page: 1,
      pageSize
    },
    fetchPolicy,
    skip
  });
  const user = selectUser(result.data as Query | undefined);
  const firstPage = user?.sessionReviews as ScoSessionReviewCollection | null | undefined;

  // Extra pages are tagged with the variables they were fetched for, so switching member (same
  // component instance, new `userId`) never shows the previous member's pages.
  const [extraState, setExtra] = useState<ExtraPages>({
    ...NO_EXTRA_PAGES,
    key: variablesKey
  });
  const extra = extraState.key === variablesKey ? extraState : {
    ...NO_EXTRA_PAGES,
    key: variablesKey
  };
  const [loadingMore, setLoadingMore] = useState(false);
  const reviews = useMemo(() => mergeSessionReviewPages((firstPage?.items ?? []) as SessionReviewItem[], extra.items), [firstPage?.items, extra.items]);
  const hasMore = extra.page > 1 ? !!extra.hasMore : !!firstPage?.pagination?.hasNextPage;
  const fetchPage = useCallback(async (page: number) => {
    const response = await client.query<Query>({
      query,
      variables: {
        ...baseVariables,
        page,
        pageSize
      },
      fetchPolicy: 'no-cache'
    });
    const collection = selectUser(response.data as Query | undefined)?.sessionReviews;
    return {
      items: (collection?.items ?? []).filter(Boolean) as SessionReviewItem[],
      hasMore: !!collection?.pagination?.hasNextPage
    };
  }, [baseVariables, client, query, pageSize, selectUser]);
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) {
      return;
    }
    setLoadingMore(true);
    try {
      const next = extra.page + 1;
      const key = variablesKey;
      const page = await fetchPage(next);
      setExtra(current => ({
        key,
        page: next,
        items: mergeSessionReviewPages(current.key === key ? current.items : [], page.items),
        hasMore: page.hasMore
      }));
    } finally {
      setLoadingMore(false);
    }
  }, [extra.page, fetchPage, hasMore, loadingMore, variablesKey]);

  /** Re-read page 1 and every extra page already shown (after a manage mutation). */
  const refetchAll = useCallback(async () => {
    const loadedPages = extra.page;
    const key = variablesKey;
    await result.refetch();
    if (loadedPages <= 1) {
      return;
    }
    let items: SessionReviewItem[] = [];
    let more = false;
    for (let page_0 = 2; page_0 <= loadedPages; page_0 += 1) {
      const next = await fetchPage(page_0);
      items = mergeSessionReviewPages(items, next.items);
      more = next.hasMore;
    }
    setExtra({
      key,
      page: loadedPages,
      items,
      hasMore: more
    });
  }, [extra.page, fetchPage, result, variablesKey]);
  return {
    user,
    reviews,
    itemCount: firstPage?.pagination?.itemCount ?? null,
    hasMore,
    loadMore,
    loadingMore,
    loading: result.loading && !result.data,
    error: result.error ?? null,
    refetchAll
  };
};